import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { AssetAmount } from "@sundaeswap/asset";
import { useHandleDepositRequest } from "@/hooks";
import { useHydrozoaHeadInfo } from "@/hooks/useHydrozoaScriptAddress";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { IntroStep } from "./IntroStep";
import { ConnectStep } from "./ConnectStep";
import { AmountStep, createDepositEntry, DepositEntry } from "./AmountStep";
import { ReviewStep } from "./ReviewStep";
import { ProcessingStep } from "./ProcessingStep";
import { SuccessStep } from "./SuccessStep";
import { formatAssetLabel, formatAssetValue } from "@/lib/marketUtils";

export type DepositStep =
  | "intro"
  | "connect"
  | "amount"
  | "review"
  | "processing"
  | "success";

interface DepositOverlayProps {
  open: boolean;
  onClose: () => void;
  onCloseStart?: () => void;
  viewRequestId?: string | null;
}

const STEP_ORDER: DepositStep[] = ["intro", "connect", "amount", "review", "processing", "success"];

function CloseIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export const DepositOverlay: FC<DepositOverlayProps> = ({
  open,
  onClose,
  onCloseStart,
  viewRequestId,
}) => {
  const [step, setStep] = useState<DepositStep>("intro");
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [animKey, setAnimKey] = useState(0);
  const [animateStep, setAnimateStep] = useState(false);
  const [closing, setClosing] = useState(false);
  const [depositEntries, setDepositEntries] = useState<DepositEntry[]>([
    createDepositEntry(),
  ]);
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const navigateTo = useCallback((next: DepositStep) => {
    const curIdx = STEP_ORDER.indexOf(step);
    const nextIdx = STEP_ORDER.indexOf(next);
    setDirection(nextIdx >= curIdx ? "forward" : "back");
    setAnimateStep(true);
    setAnimKey((k) => k + 1);
    setStep(next);
  }, [step]);
  const { activeWallet } = useWalletObserver();
  const { mutateAsync } = useHandleDepositRequest();
  const { depositTxs, deposits, accounts } = useGummiwormContext();
  const hasL2Funds = accounts.length > 0;
  const headInfo = useHydrozoaHeadInfo();
  const headOnline = !!headInfo;

  const isConnected = !!activeWallet;

  // When opened, decide which step to start on (only on open/viewRequestId change)
  useEffect(() => {
    if (!open) return;
    setAnimateStep(false);
    if (viewRequestId) {
      setActiveRequestId(viewRequestId);
      setStep("processing");
    } else {
      setStep("intro");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, viewRequestId]);

  // When wallet connects while on the connect step, advance to amount
  useEffect(() => {
    if (step === "connect" && isConnected) {
      setStep("amount");
    }
  }, [step, isConnected]);

  const handleClose = useCallback(() => {
    setClosing(true);
    onCloseStart?.();
    setTimeout(() => {
      setClosing(false);
      setAnimateStep(false);
      setStep("intro");
      setDepositEntries([createDepositEntry()]);
      setActiveRequestId(null);
      setError(null);
      onClose();
    }, 200);
  }, [onClose, onCloseStart]);

  const handleBeginDeposit = useCallback(() => {
    if (isConnected) {
      navigateTo("amount");
    } else {
      navigateTo("connect");
    }
  }, [isConnected, navigateTo]);

  const handleAmountConfirm = useCallback(() => {
    navigateTo("review");
  }, [navigateTo]);

  const handleDeposit = useCallback(async () => {
    setError(null);
    navigateTo("processing");
    try {
      const amounts = depositEntries
        .filter((e) => Number(e.amount) > 0)
        .map((e) =>
          AssetAmount.fromValue(Number(e.amount), {
            decimals: 6,
            assetId: e.assetId,
          }),
        );
      const result = await mutateAsync({ depositAmounts: amounts });
      const id = (result as { requestId: number }).requestId.toString();
      setActiveRequestId(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      navigateTo("review");
    }
  }, [depositEntries, mutateAsync, navigateTo]);

  const handleBack = useCallback(() => {
    if (step === "connect") {
      navigateTo("intro");
      return;
    }
    if (step === "amount") {
      navigateTo("intro");
      return;
    }
    if (step === "review") {
      navigateTo("amount");
    }
  }, [navigateTo, step]);

  const activeTx = activeRequestId
    ? depositTxs.get(activeRequestId)
    : null;
  // Derive status from SpacetimeDB deposit row if no local tracking exists
  const activeStatus = activeTx?.status ?? (() => {
    if (!activeRequestId) return null;
    const dep = deposits.find((d) => d.requestId.toString() === activeRequestId);
    if (!dep) return null;
    if (dep.absorbedInBlock) return "absorbed" as const;
    if (dep.rejectedInBlock) return "rejected" as const;
    if (dep.failureReason) return "failed" as const;
    if (dep.refundTx) return "confirmed" as const;
    return "pending" as const;
  })();

  const primaryDepositAmount = depositEntries[0]?.amount ?? "";

  const effectiveAmountLabel = useMemo(() => {
    if (!activeRequestId) return primaryDepositAmount ? `${primaryDepositAmount} ADA` : "";
    const dep = deposits.find(
      (d) => d.requestId.toString() === activeRequestId,
    );
    if (!dep) return primaryDepositAmount ? `${primaryDepositAmount} ADA` : "";
    const assets = dep.value.assets;
    if (assets.length === 0) return "";
    const first = assets[0];
    const primary = `${formatAssetValue(first.asset, first.value)} ${formatAssetLabel(first.asset)}`;
    return assets.length > 1
      ? `${primary} + ${assets.length - 1} other${assets.length > 2 ? "s" : ""}`
      : primary;
  }, [activeRequestId, deposits, primaryDepositAmount]);

  // ProcessingStep now handles the complete state directly (shows "Funds Deposited!")

  // Build the ordered list of steps for this session
  const steps = useMemo<DepositStep[]>(() => {
    const s: DepositStep[] = ["intro"];
    if (!isConnected) s.push("connect");
    s.push("amount", "review", "processing", "success");
    return s;
  }, [isConnected]);

  const currentIndex = steps.indexOf(step);
  const showBackButton =
    currentIndex > 0 &&
    (step === "connect" || step === "amount" || step === "review");

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, handleClose]);

  if (!open) return null;

  return (
    <div
      className="sr-overlay"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        aria-hidden="true"
        className={`sr-overlay-backdrop ${closing ? "sr-overlay-backdrop-exit" : "sr-overlay-backdrop-enter"}`}
      />
      <button
        onClick={handleClose}
        className="fixed right-4 top-4 z-20 rounded-full border border-white/[0.03] bg-sr-surface p-2 text-sr-neutral-500 transition-colors hover:text-white sm:right-6 sm:top-6"
        aria-label="Close deposit flow"
      >
        <CloseIcon />
      </button>
      <div
        key={animKey}
        className={`relative z-10 w-full max-w-[420px] flex flex-col gap-8 items-center ${closing ? "sr-overlay-content-exit" : "sr-overlay-content-enter"}`}
      >
        <div
          className={`w-full flex flex-col gap-8 items-center ${animateStep ? (direction === "forward" ? "sr-step-forward" : "sr-step-back") : ""}`}
        >
          {step === "intro" && (
            <IntroStep onBegin={handleBeginDeposit} isConnected />
          )}
          {step === "connect" && (
            <ConnectStep onBack={showBackButton ? handleBack : undefined} />
          )}
          {step === "amount" && (
            <AmountStep
              entries={depositEntries}
              onEntriesChange={setDepositEntries}
              onConfirm={handleAmountConfirm}
              onBack={showBackButton ? handleBack : undefined}
            />
          )}
          {step === "review" && (
            <ReviewStep
              entries={depositEntries}
              error={error}
              headOnline={headOnline}
              onConfirm={handleDeposit}
              onBack={showBackButton ? handleBack : undefined}
            />
          )}
          {step === "processing" && (
            <ProcessingStep
              requestId={activeRequestId}
              status={activeStatus ?? null}
              error={error}
              estimatedAbsorptionTime={activeTx?.estimatedAbsorptionTime}
              amountLabel={effectiveAmountLabel}
              txHash={activeTx?.hash}
            />
          )}
          {step === "success" && (
            <SuccessStep amountLabel={effectiveAmountLabel} onClose={handleClose} />
          )}

          {/* Step indicator dots (hidden during processing/success) */}
          {step !== "processing" && step !== "success" && (
            <div className="flex gap-1 items-center justify-center">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={i === currentIndex ? "sr-dot-active" : "sr-dot"}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
